import { ImageUploadModal } from "@/components/avatar-upload/ImageUploadModal.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EventEditModal } from "@/components/event/EventEditModal.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { TeamsListPTeamPageSidePanel } from "@/pages/employees/team/components/TeamPageSidePanel";
import { useTitle } from "@/hooks/use-title.hook.ts";
import TeamTabOverview from "@/pages/employees/team/components/TeamTabOverview.tsx";
import TeamEditModal from "@/pages/employees/team/widgets/TeamEditModal.tsx";

import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import {
  useTeamDelete,
  useTeamDeleteBackground,
  useTeamDetails,
  useTeamSetBackground,
} from "@/queries/use-teams.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useAppTheme } from "@/themes/app-context.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { getMockEventEdit } from "@/types/event.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { RuleApplySignType, RuleConditionType } from "@/types/rule.types.ts";
import { ApiTeam } from "@/types/team.types.ts";
import {
  Avatar,
  Box,
  Button,
  ButtonLink,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  Page,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { parseInt } from "lodash";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export default function TeamPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const modalsService = useModals();
  const body = useAppTheme("body");

  const teamSetBackground = useTeamSetBackground();
  const teamDeleteBackground = useTeamDeleteBackground();
  const teamDelete = useTeamDelete();

  const { canIEdit } = usePermissions();
  const { data: me } = useEmployeeMe();
  const { data: team, isLoading } = useTeamDetails(id ? parseInt(id, 10) : undefined);

  useTitle(team ? team.title : undefined, t("core:teams"));

  const canEditTeam = canIEdit(ApiPermission.manage_teams);

  useEffect(() => {
    if (!id) return;

    analyticsService.trackEvent(analyticEvents.team.teamViewed, {
      [analyticProperties.id]: id,
    });
  }, [id]);

  const handleNewTeam = useCallback(() => {
    modalsService.openModal(TeamEditModal);
  }, []);

  const handleCreateMeeting = (team: ApiTeam) => {
    if (!me) return;

    const eventToEdit = getMockEventEdit(me);
    eventToEdit.attendeeGroups = [
      {
        rules: [
          {
            condition_type: RuleConditionType.team,
            sign: RuleApplySignType.include,
            condition_ids: [team.id],
            condition_entities: [{ id: team.id, title: team.title }],
            value_entities: [],
          },
        ],
      },
    ];

    modalsService.openModal(EventEditModal, { event: eventToEdit });
  };

  const handleEditTeam = useCallback((team: ApiTeam) => {
    modalsService.openModal(TeamEditModal, { team });
  }, []);

  const handleEditBackground = useCallback((team: ApiTeam) => {
    modalsService.openModal(ImageUploadModal, {
      modalTitle: t("common:background"),
      path: ApiFilePathTypes.team,
      onSave: async (file: ApiFile) => {
        try {
          await teamSetBackground.mutateAsync({
            id: team.id,
            fileUUID: file.uuid,
          });

          analyticsService.trackEvent(analyticEvents.team.teamEdited, {
            [analyticProperties.id]: team.id,
            [analyticProperties.actionType]: "Background Updated",
          });
          return true;
        } catch {
          return false;
        }
      },
    });
  }, []);

  const handleDeleteBackground = useCallback(async (team: ApiTeam) => {
    try {
      await teamDeleteBackground.mutateAsync(team.id);

      analyticsService.trackEvent(analyticEvents.team.teamEdited, {
        [analyticProperties.actionType]: "Background Deleted",
      });
    } catch {
      return;
    }
  }, []);

  const handleDelete = useCallback((team: ApiTeam) => {
    modalsService.openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("core:delete_team_question"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await teamDelete.mutateAsync(team.id);

          analyticsService.trackEvent(analyticEvents.team.teamDeleted, {
            [analyticProperties.id]: team.id,
          });

          navigate("/employees/teams");
        } catch {
          return;
        }
      },
    });
  }, []);

  const renderCurrentSection = () => {
    if (!team) return null;
    return <TeamTabOverview team={team} />;
  };

  return (
    <Box className={"h-full flex overflow-hidden"}>
      <Box
        className={
          "w-76 h-full shrink-0 shadow-sidebar relative overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable] bg-light/80"
        }
      >
        <TeamsListPTeamPageSidePanel />
      </Box>

      <Page className={"h-full overflow-auto relative flex-1"}>
        <div className={twMerge(body.base, "relative h-48")}>
          {team && team.background_url ? (
            <img
              src={team.background_url}
              alt={t("common:background")}
              className="w-full h-full object-cover absolute inset-0"
            />
          ) : (
            <div className="w-full h-full absolute inset-0 bg-light/80 to-transparent" />
          )}
          <div className="w-full h-full absolute inset-0 bg-linear-to-b from-white/50 to-transparent" />

          <Stack className="relative z-10 h-full">
            <Page.Header title={t("core:team_profile")}>
              <Stack
                direction={"horizontal"}
                gap={"sm"}
                items={"center"}
                className={"overflow-hidden"}
              >
                {canEditTeam && (
                  <Button
                    onClick={handleNewTeam}
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
                  >
                    {t("common:create_new")}
                  </Button>
                )}

                <DropdownMenu
                  trigger={
                    <Button disabled={!team}>
                      <FontAwesomeIcon icon="fa-light fa-ellipsis" />
                    </Button>
                  }
                >
                  {team && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleCreateMeeting(team)}
                        title={t("calendar:create_meeting")}
                        description={t("calendar:create_meeting_for_team", {
                          name: team.title,
                        })}
                      />
                      {canEditTeam && (
                        <>
                          <hr />
                          <DropdownMenuItem
                            onClick={() => handleEditTeam(team)}
                            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                            title={t("common:edit")}
                          />
                          <DropdownMenuItem
                            onClick={() => handleEditBackground(team)}
                            leftIcon={<FontAwesomeIcon icon="fa-light fa-image" />}
                            title={t("core:change_background")}
                          />
                          {team.background_url && (
                            <DropdownMenuItem
                              variant={"menuDanger"}
                              onClick={() => handleDeleteBackground(team)}
                              leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                              title={t("core:remove_background")}
                            />
                          )}
                          <hr />
                          <DropdownMenuItem
                            onClick={() => handleDelete(team)}
                            leftIcon={<FontAwesomeIcon icon="fa-light fa-users-slash" />}
                            variant={"menuDanger"}
                            title={`${t("core:delete_team")}...`}
                          />
                        </>
                      )}
                    </>
                  )}
                </DropdownMenu>
              </Stack>
            </Page.Header>

            {team ? (
              <Page.Content className="relative">
                <Stack items={"center"}>
                  <Stack items={"center"} gap={"md"}>
                    <Box className={"relative  bg-light rounded-sm"}>
                      <Avatar
                        variant={"default"}
                        emoji={team.emoji}
                        size={"2xl"}
                        className={"shadow-md"}
                      />
                    </Box>
                    <Stack items={"center"} className={"text-center"}>
                      <Title header={team.title} size={"lg"} />
                      {team.description && (
                        <span
                          className={"text-secondary whitespace-pre-wrap text-lg"}
                          dangerouslySetInnerHTML={{ __html: team.description }}
                        />
                      )}
                      {team.links.length > 0 && (
                        <Stack
                          className={"pt-4 flex-wrap justify-center"}
                          direction={"horizontal"}
                          gap={"sm"}
                        >
                          {team.links.map((link, index) => (
                            <ButtonLink
                              key={`link-item-${index}-${team.id}-${link.title}`}
                              href={link.url}
                              blank={true}
                            >
                              {link.title}
                            </ButtonLink>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </Stack>

                <Stack className={"mt-19"}>{renderCurrentSection()}</Stack>
              </Page.Content>
            ) : (
              <Page.Content className="relative">
                {isLoading ? (
                  <>
                    <Stack items={"center"}>
                      <Stack items={"center"} gap={"lg"}>
                        <Skeleton className={"w-41 h-41 rounded-lg"} />
                        <Stack items={"center"} gap={"sm"}>
                          <Skeleton className={"w-40 h-4"} />
                          <Skeleton className={"w-40 h-4"} />
                        </Stack>
                      </Stack>
                      <Stack direction={"horizontal"} gap={"2xl"} className={"w-full mt-32 h-70"}>
                        {Array.from({ length: 6 }).map((_, index) => (
                          <Stack key={index} className={"w-40"} gap={"sm"}>
                            <Skeleton className={"w-41 h-41 rounded-lg"} />
                            <Skeleton className={"w-40 h-4"} />
                            <Skeleton className={"w-40 h-4"} />
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                  </>
                ) : (
                  <Box className={"mt-32"}>
                    <EmptyStateBase
                      emoji={emojiDefaults.team.default}
                      title={t("core:no_team_created")}
                      description={t("core:no_teams_created_description")}
                      primaryButton={
                        canEditTeam ? (
                          <Button
                            onClick={handleNewTeam}
                            size={"sm"}
                            variant={"secondary"}
                            leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                          >
                            {t("core:create_team")}
                          </Button>
                        ) : null
                      }
                    />
                  </Box>
                )}
              </Page.Content>
            )}
          </Stack>
        </div>
      </Page>
    </Box>
  );
}
